import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Loader2, Wallet, Receipt, Info, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import PrimaryButton from '@/components/ui/PrimaryButton';
import CostFormModal, { CostPayload, RECURRENCE_OPTS } from '@/components/salon/CostFormModal';
import {
    fetchShopCostsService,
    createShopCostService,
    updateShopCostService,
    deleteShopCostService,
    monthlyizeCost,
    ShopCost,
} from '@/services/shop/shopCostsService';

const euro = (n: number) => `€ ${Math.round(n).toLocaleString('it-IT')}`;

const recurrenceLabel = (c: ShopCost) =>
    c.recurrence === 'custom_days'
        ? `ogni ${c.interval_days || '?'} gg`
        : RECURRENCE_OPTS.find((o) => o.value === c.recurrence)?.label || c.recurrence;

type SortKey = 'name' | 'recurrence' | 'amount' | 'monthly' | 'start';

interface Row {
    cost: ShopCost;
    name: string;
    category: string | null;
    note: string | null;
    recurrenceIdx: number;
    recurrenceText: string;
    amount: number;
    monthly: number;
    startTs: number;
    startText: string;
}

const SalonCosts: React.FC = () => {
    const { organization } = useAuth();
    const [costs, setCosts] = useState<ShopCost[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [toDelete, setToDelete] = useState<ShopCost | null>(null);

    // Modal crea/modifica: `editing` presente ⇒ modalità modifica.
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<ShopCost | null>(null);

    const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'monthly', dir: 'desc' });

    const load = async () => {
        if (!organization?.id) return;
        setLoading(true);
        const res = await fetchShopCostsService(Number(organization.id));
        if (res.success) setCosts(res.data);
        setLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization?.id]);

    const monthlyTotal = useMemo(() => costs.reduce((s, c) => s + monthlyizeCost(c), 0), [costs]);

    const rows: Row[] = useMemo(
        () =>
            costs.map((c) => ({
                cost: c,
                name: c.name,
                category: c.category,
                note: c.note,
                recurrenceIdx: RECURRENCE_OPTS.findIndex((o) => o.value === c.recurrence),
                recurrenceText: recurrenceLabel(c),
                amount: Number(c.amount) || 0,
                monthly: monthlyizeCost(c),
                startTs: c.start_date ? new Date(`${c.start_date}T00:00:00`).getTime() : 0,
                startText: c.start_date ? new Date(c.start_date).toLocaleDateString('it-IT') : '—',
            })),
        [costs],
    );

    const sortedRows = useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => {
            let cmp: number;
            switch (sort.key) {
                case 'name':
                    cmp = a.name.localeCompare(b.name);
                    break;
                case 'recurrence':
                    cmp = a.recurrenceIdx - b.recurrenceIdx;
                    break;
                case 'amount':
                    cmp = a.amount - b.amount;
                    break;
                case 'monthly':
                    cmp = a.monthly - b.monthly;
                    break;
                case 'start':
                    cmp = a.startTs - b.startTs;
                    break;
                default:
                    cmp = 0;
            }
            return sort.dir === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [rows, sort]);

    const toggleSort = (key: SortKey) => {
        setSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' || key === 'recurrence' ? 'asc' : 'desc' },
        );
    };

    const columns: { key: SortKey; label: string; align: 'left' | 'right' }[] = [
        { key: 'name', label: 'Nome', align: 'left' },
        { key: 'recurrence', label: 'Ricorrenza', align: 'left' },
        { key: 'amount', label: 'Importo', align: 'right' },
        { key: 'monthly', label: 'Mensile', align: 'right' },
        { key: 'start', label: 'Dal', align: 'right' },
    ];

    const openNew = () => {
        setEditing(null);
        setModalOpen(true);
    };
    const openEdit = (c: ShopCost) => {
        setEditing(c);
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
        setEditing(null);
    };

    const handleSubmit = async (payload: CostPayload) => {
        if (!organization?.id) return;
        setIsSaving(true);
        const res = editing
            ? await updateShopCostService(editing.id, payload)
            : await createShopCostService({ shop_id: Number(organization.id), ...payload });
        if (res.success) {
            toast.success(editing ? 'Costo aggiornato' : 'Costo aggiunto');
            closeModal();
            await load();
        } else {
            toast.error(res.error || 'Errore nel salvataggio');
        }
        setIsSaving(false);
    };

    const handleDelete = async () => {
        if (!toDelete) return;
        const res = await deleteShopCostService(toDelete.id);
        if (res.success) {
            setCosts((prev) => prev.filter((c) => c.id !== toDelete.id));
        } else {
            toast.error('Errore eliminazione');
        }
        setToDelete(null);
    };

    return (
        <div className="space-y-6">
            {/* Header sezione: azione fuori dalla box del riepilogo */}
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-lg font-bold text-gray-900">Costi del salone</h2>
                <PrimaryButton label="Aggiungi costo" icon={Plus} variant="primary" showLabelOnMobile onClick={openNew} />
            </div>

            {/* Riepilogo costi mensili */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-clip-50 text-clip-500 flex items-center justify-center shrink-0">
                        <Wallet size={20} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Costi fissi mensilizzati</p>
                        <p className="text-2xl font-bold text-gray-900">
                            {euro(monthlyTotal)}
                            <span className="text-sm font-medium text-gray-400"> /mese</span>
                        </p>
                    </div>
                </div>
                <p className="text-xs text-gray-400 mt-4 flex items-center gap-1.5">
                    <Info size={13} className="text-gray-300 shrink-0" />
                    Le voci "una tantum" non rientrano nel totale mensile.
                </p>
            </div>

            {/* Tabella costi */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-100 bg-gray-50/50">
                                {columns.map((col) => (
                                    <th
                                        key={col.key}
                                        onClick={() => toggleSort(col.key)}
                                        className={`px-4 py-3 font-bold text-gray-500 uppercase tracking-wider text-xs cursor-pointer select-none hover:text-gray-900 transition-colors ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                                    >
                                        <span className={`inline-flex items-center gap-1.5 ${col.align === 'right' ? 'flex-row-reverse' : ''}`}>
                                            {col.label}
                                            <ArrowUpDown size={12} className={sort.key === col.key ? 'text-clip-500' : 'text-gray-300'} />
                                        </span>
                                    </th>
                                ))}
                                <th className="px-4 py-3 w-12" aria-label="Azioni" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length + 1} className="px-4 py-10 text-center text-gray-400">
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 size={18} className="animate-spin" /> Carico i costi…
                                        </span>
                                    </td>
                                </tr>
                            ) : sortedRows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length + 1} className="px-4 py-10 text-center text-gray-400">
                                        Nessun costo registrato. Aggiungi affitto, utenze, forniture e altre spese con "Aggiungi costo".
                                    </td>
                                </tr>
                            ) : (
                                sortedRows.map((r) => (
                                    <tr
                                        key={r.cost.id}
                                        onClick={() => openEdit(r.cost)}
                                        className="border-b border-gray-50 last:border-0 hover:bg-clip-50/40 transition-colors cursor-pointer"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-xl bg-clip-100 text-clip-600 flex items-center justify-center shrink-0">
                                                    <Receipt size={16} />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate">{r.name}</p>
                                                    {(r.category || r.note) && (
                                                        <p className="text-xs text-gray-400 truncate">
                                                            {[r.category, r.note].filter(Boolean).join(' · ')}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700">{r.recurrenceText}</td>
                                        <td className="px-4 py-3 text-right font-bold text-gray-900">{euro(r.amount)}</td>
                                        <td className="px-4 py-3 text-right text-gray-700">
                                            {r.cost.recurrence !== 'one_off' && r.monthly > 0 ? `${euro(r.monthly)}/mese` : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-500">{r.startText}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setToDelete(r.cost); }}
                                                title="Elimina costo"
                                                className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal crea/modifica costo */}
            <CostFormModal
                isOpen={modalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                saving={isSaving}
                initial={editing}
            />

            <ConfirmDialog
                isOpen={!!toDelete}
                title="Elimina costo"
                description={`Vuoi eliminare "${toDelete?.name}"? L'azione è irreversibile.`}
                confirmLabel="Elimina"
                variant="danger"
                onConfirm={handleDelete}
                onCancel={() => setToDelete(null)}
            />
        </div>
    );
};

export default SalonCosts;
